import React from "react";
import type { OptionTs } from "@ps-bridge/interop";
import * as Option from "@utils/Option";
import type { Ok } from "@ps-bridge/ok";
import PageCounterPanel from "@view/panels/workspace/page-counter/PageCounter.panel";
import type { PageCounterPhase, PageCounterPhasePayload } from "@view/panels/workspace/page-counter/PageCounterPhase";
import * as styles from "@styles/PageCounter.css";

export type PageCounterViewProps = {
  pageCounterRouteLabel: string;
  pdfGuidance: string;
  uploadLabel: string;
  retryLabel: string;
  selectedFileName: OptionTs<string>;
  pageCounterPhase: PageCounterPhase;
  phasePayload: PageCounterPhasePayload[PageCounterPhase];
  canSubmit: boolean;
  onFileChange: (value: OptionTs<File>) => Ok;
  onSubmit: () => Ok;
};

const PageCounterComponent: React.FC<PageCounterViewProps> = ({
  pageCounterRouteLabel,
  pdfGuidance,
  uploadLabel,
  retryLabel,
  selectedFileName,
  pageCounterPhase,
  phasePayload,
  canSubmit,
  onFileChange,
  onSubmit,
}) => (
  <section className={styles.page} data-testid="page-page-counter" data-page-state="ready">
    <div className={styles.shell}>
      <header className={styles.hero}>
        <p className={styles.eyebrow}>{pageCounterRouteLabel}</p>
        <h1 className={styles.title}>Count pages before you bill.</h1>
        <p className={styles.copy}>{pdfGuidance}</p>
        <ul className={styles.processList}>
          <li className={styles.processItem}>
            <span className={styles.processBadge}>1</span>
            Upload a document through the workspace upload path.
          </li>
          <li className={styles.processItem}>
            <span className={styles.processBadge}>2</span>
            Count billable pages for PDFs.
          </li>
          <li className={styles.processItem}>
            <span className={styles.processBadge}>3</span>
            Return the page count and billing cost from the backend.
          </li>
        </ul>
      </header>
      <div className={styles.card}>
        <label className={styles.uploadZone}>
          <span className={styles.uploadLabel}>{uploadLabel}</span>
          <span className={styles.uploadHint}>Choose any document. Only PDFs are page counted in this first version.</span>
          <input
            aria-label={uploadLabel}
            className={styles.input}
            type="file"
            onChange={(event) => {
              const selected = event.target.files?.[0];
              onFileChange(selected ? Option.some(selected) : Option.none<File>());
            }}
          />
        </label>
        <PageCounterPanel
          phase={pageCounterPhase}
          payload={phasePayload as never}
        />
        <p className={styles.selected}>{Option.fold(selectedFileName, () => "No file selected.", (value) => value)}</p>
        <button className={styles.button} disabled={!canSubmit} onClick={() => onSubmit()}>{retryLabel}</button>
      </div>
    </div>
  </section>
);

export default PageCounterComponent;
